import { FileButton } from '../components/FileButton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDemo } from '../DemoContext';

/**
 * Cloud GPU — the same view as cloud.vast.ai/instances, inside TheVisionQC.
 *
 * Shows the training account's credit and any rented GPU instances (live, polled),
 * and lets an admin stage a COCO dataset for a model. The Vast.ai key stays on the
 * backend; this only reads /api/gpu/*. Instances are usually empty at rest — pods
 * are destroyed the moment a training finishes, so this fills in during a run.
 */

interface Instance {
  id: number;
  gpu: string;
  numGpus: number;
  status: string;
  dphTotal: number;
  gpuUtil: number | null;
  startDate: number | null;
}
interface Overview {
  configured: boolean;
  email?: string | null;
  credit: number | null;
  instances: Instance[];
  error?: string;
}
interface Dataset {
  id: string;
  modelKind: string;
  name: string;
  sizeBytes: number;
  classes: string | null;
  createdAt: string;
  uploadedBy: string | null;
}
interface Job {
  id: string;
  modelKind: string;
  datasetName: string | null;
  status: string;
  phase: string | null;
  gpu: string | null;
  dph: number | null;
  cost: number | null;
  numClasses: number | null;
  log: string | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

const JOB_TONE: Record<string, string> = {
  done: 'var(--good)',
  failed: 'var(--bad)',
  queued: 'var(--muted)',
};

const KINDS = ['fire', 'forklift', 'ppe', 'other'] as const;

function fmtSize(raw: number): string {
  const n = Number(raw) || 0; // pg returns bigint as a string
  if (n > 1e9) return `${(n / 1e9).toFixed(1)} GB`;
  if (n > 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${(n / 1e3).toFixed(0)} KB`;
}
// pg returns numeric/bigint columns as strings; coerce before formatting so a
// stray string never throws `toFixed is not a function` and blanks the page.
const money = (v: unknown, d = 3): string => (Number(v) || 0).toFixed(d);
function uptime(startSec: number | null): string {
  if (!startSec) return '—';
  const s = Math.max(0, Date.now() / 1000 - startSec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}u ${m}m` : `${m}m`;
}

export function GpuTrainingView() {
  const { demo, showPaid } = useDemo();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<(typeof KINDS)[number]>('fire');
  const [name, setName] = useState('');
  const [classes, setClasses] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const loadOverview = useCallback(() => {
    fetch('/api/gpu/overview')
      .then((r) => (r.ok ? r.json() : null))
      .then(setOverview)
      .catch(() => undefined);
  }, []);
  const loadDatasets = useCallback(() => {
    fetch('/api/gpu/datasets')
      .then((r) => (r.ok ? r.json() : []))
      .then(setDatasets)
      .catch(() => undefined);
  }, []);
  const loadJobs = useCallback(() => {
    fetch('/api/gpu/jobs')
      .then((r) => (r.ok ? r.json() : []))
      .then(setJobs)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadOverview();
    loadDatasets();
    loadJobs();
    // Poll faster while anything is live, so status/phase/cost move in near-real-time.
    const t = setInterval(() => { loadOverview(); loadJobs(); }, 6_000);
    return () => clearInterval(t);
  }, [loadOverview, loadDatasets, loadJobs]);

  const train = useCallback(
    async (datasetId: string) => {
      const res = await fetch('/api/gpu/train', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ datasetId }),
      });
      if (!res.ok) setError(((await res.json()) as { message?: string }).message ?? 'Could not start training');
      loadJobs();
    },
    [loadJobs],
  );

  const stopJob = useCallback(
    async (id: string) => {
      await fetch(`/api/gpu/jobs/${id}/stop`, { method: 'POST' }).catch(() => undefined);
      loadJobs();
    },
    [loadJobs],
  );

  const upload = useCallback(async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) {
      setError('Choose a dataset .zip first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('dataset', f, f.name);
      form.append('modelKind', kind);
      form.append('name', name || f.name);
      if (classes) form.append('classes', classes);
      const res = await fetch('/api/gpu/datasets', { method: 'POST', body: form });
      if (!res.ok) throw new Error(((await res.json()) as { message?: string }).message ?? 'Upload failed');
      setName('');
      setClasses('');
      if (fileRef.current) fileRef.current.value = '';
      loadDatasets();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [kind, name, classes, loadDatasets]);

  const remove = useCallback(
    async (id: string) => {
      if (!confirm('Remove this staged dataset? This cannot be undone.')) return;
      await fetch(`/api/gpu/datasets/${id}`, { method: 'DELETE' });
      loadDatasets();
    },
    [loadDatasets],
  );

  const instances = overview?.instances ?? [];

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Cloud GPU</h1>
        <Link to="/safety" style={{ fontSize: 13 }}>← Safety</Link>
      </div>
      <p className="muted" style={{ marginTop: -8, maxWidth: 760 }}>
        Train the vision models on our cloud GPUs — a fire, forklift or PPE model trains in
        minutes. Stage a dataset below, hit train, and the finished model deploys itself. A GPU
        spins up only for the run and is released the moment it finishes.
      </p>

      {overview && !overview.configured && (
        <div className="panel" style={{ color: 'var(--warn)', marginBottom: 16 }}>
          The training cloud isn't connected yet. The panel shows datasets only until it is.
        </div>
      )}
      {overview?.error && (
        <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>
          Training cloud error: {overview.error}
        </div>
      )}

      {overview?.configured && (
        <div className="row" style={{ marginBottom: 16, gap: 12 }}>
          <div className="panel" style={{ minWidth: 160 }}>
            <div className="muted" style={{ fontSize: 12 }}>Cloud balance</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>
              {overview.credit != null ? `$${money(overview.credit, 2)}` : '—'}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>TheVisionQC training cloud</div>
          </div>
          <div className="panel" style={{ minWidth: 160 }}>
            <div className="muted" style={{ fontSize: 12 }}>Instances running</div>
            <div style={{ fontSize: 22, fontWeight: 600, color: instances.length ? 'var(--teal)' : undefined }}>
              {instances.length}
            </div>
            <div className="muted" style={{ fontSize: 11 }}>
              ${money(instances.reduce((s, i) => s + (Number(i.dphTotal) || 0), 0))}/hr total
            </div>
          </div>
        </div>
      )}

      {/* Live instances */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <strong style={{ fontSize: 13 }}>GPU instances</strong>
        {instances.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
            Idle — a GPU is released after each run, so this fills in only while training is live.
          </div>
        ) : (
          <div className="grid" style={{ gap: 8, marginTop: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {instances.map((i) => (
              <div key={i.id} className="panel" style={{ padding: 12, borderLeft: '3px solid var(--teal)' }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <strong>{i.numGpus}× {i.gpu}</strong>
                  <span className="muted" style={{ fontSize: 12 }}>#{i.id}</span>
                </div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  status: <strong style={{ color: i.status === 'running' ? 'var(--good)' : 'var(--warn)' }}>{i.status}</strong>
                  {i.gpuUtil != null && ` · GPU ${Math.round(i.gpuUtil)}%`}
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  ${money(i.dphTotal)}/hr · up {uptime(i.startDate)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      {/* Dataset upload */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <strong style={{ fontSize: 13 }}>Upload a training dataset</strong>
        <div className="muted" style={{ fontSize: 12, marginTop: 4, marginBottom: 10 }}>
          A Roboflow <strong>COCO</strong> export (a .zip with train/ and valid/). Pick which model it
          trains; the class list is optional (shown in the list so you can tell datasets apart).
        </div>
        <div className="grid" style={{ gap: 8, maxWidth: 520 }}>
          <div className="row" style={{ gap: 8 }}>
            <select value={kind} onChange={(e) => setKind(e.target.value as (typeof KINDS)[number])}>
              {KINDS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
            <input
              placeholder="Name (e.g. fire+smoke v2)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
          <input placeholder="Classes, optional (e.g. fire, smoke)" value={classes} onChange={(e) => setClasses(e.target.value)} />
          <FileButton inputRef={fileRef} accept=".zip" label="Choose .zip" />
          <button className="btn primary" onClick={() => void upload()} disabled={busy}>
            {busy ? 'Uploading…' : 'Upload dataset'}
          </button>
        </div>
      </div>

      {/* Dataset list */}
      <div className="panel">
        <strong style={{ fontSize: 13 }}>Staged datasets</strong>
        {datasets.length === 0 ? (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>No datasets yet.</div>
        ) : (
          <div className="grid" style={{ gap: 6, marginTop: 10 }}>
            {datasets.map((d) => (
              <div key={d.id} className="row" style={{ justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ minWidth: 0 }}>
                  <span className="pill" style={{ marginRight: 8 }}>{d.modelKind}</span>
                  <strong>{d.name}</strong>
                  {d.classes && <span className="muted" style={{ fontSize: 12 }}> — {d.classes}</span>}
                  <div className="muted" style={{ fontSize: 11 }}>
                    {fmtSize(d.sizeBytes)} · {new Date(d.createdAt).toLocaleString('nl-NL')}
                    {d.uploadedBy ? ` · ${d.uploadedBy}` : ''}
                  </div>
                </div>
                <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                  {d.modelKind !== 'other' && (
                    <button
                      className="btn sm primary"
                      onClick={() => (demo ? showPaid() : void train(d.id))}
                      title={demo ? 'Available in the full version' : 'Train and deploy — one click'}
                    >
                      Train in cloud
                    </button>
                  )}
                  <button className="btn sm" onClick={() => void remove(d.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Training jobs */}
      {jobs.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <strong style={{ fontSize: 13 }}>Training jobs</strong>
          <div className="grid" style={{ gap: 8, marginTop: 10 }}>
            {jobs.map((j) => {
              const tone = JOB_TONE[j.status] ?? 'var(--teal)';
              const live = !['done', 'failed'].includes(j.status);
              return (
                <div key={j.id} className="panel" style={{ padding: 12, borderLeft: `3px solid ${tone}` }}>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <span className="pill" style={{ marginRight: 8 }}>{j.modelKind}</span>
                      <strong>{j.datasetName ?? 'dataset'}</strong>
                      <span style={{ color: tone, fontSize: 13, marginLeft: 8 }}>
                        {live && <span className="spin" style={{ marginRight: 4 }}>◍</span>}{j.status}
                      </span>
                    </div>
                    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <span className="muted" style={{ fontSize: 12 }}>
                        {j.gpu ?? ''}{j.dph != null ? ` · $${money(j.dph)}/hr` : ''}
                        {j.cost != null ? ` · ~$${money(j.cost)}` : ''}
                      </span>
                      {live && (
                        <button className="btn sm" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }} onClick={() => void stopJob(j.id)}>
                          ■ Stop
                        </button>
                      )}
                    </div>
                  </div>
                  {j.phase && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{j.phase}</div>}
                  {j.error && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 4 }}>{j.error}</div>}
                  {j.log && (
                    <pre style={{ fontSize: 11, marginTop: 8, maxHeight: 120, overflow: 'auto', background: 'var(--bg-soft, rgba(0,0,0,0.15))', padding: 8, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                      {j.log}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
